import { useSyncExternalStore } from 'react'

const STORAGE_KEY = 'storeLayoutPreference'

export type StoreLayoutPreference = 'standard' | 'experiment-1'

const VALID_PREFERENCES: StoreLayoutPreference[] = ['standard', 'experiment-1']

/**
 * Persisted layout preference for authenticated store-owner pages.
 *
 * A/B system:
 * - `standard`      — Default: responsive mobile-style centered column
 *                     (430px mobile → 640px tablet → 768px PC). No sidebar.
 * - `experiment-1`  — Wide SaaS-style left sidebar with store switcher;
 *                     opt-in for owners who prefer desktop-style navigation.
 *
 * Backed by a module-level store + useSyncExternalStore so every consumer
 * (App, StoreLayout, StoreSidebar, the preferences page) re-renders in unison
 * when setPreference is called — independent useState instances would not.
 */
function migrate(raw: string | null): StoreLayoutPreference {
  if (raw && (VALID_PREFERENCES as string[]).includes(raw)) {
    return raw as StoreLayoutPreference
  }
  if (raw === 'wide') return 'experiment-1'
  if (raw === 'compact' || raw === 'experiment-2') return 'standard'
  return 'standard'
}

function readFromStorage(): StoreLayoutPreference {
  try {
    return migrate(localStorage.getItem(STORAGE_KEY))
  } catch {
    return 'standard'
  }
}

let cachedValue: StoreLayoutPreference | null = null
const listeners = new Set<() => void>()

function getSnapshot(): StoreLayoutPreference {
  if (cachedValue === null) cachedValue = readFromStorage()
  return cachedValue
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

export function setLayoutPreference(next: StoreLayoutPreference): void {
  cachedValue = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // ignore quota / disabled storage
  }
  listeners.forEach((fn) => fn())
}

export function useLayoutPreference() {
  const preference = useSyncExternalStore(subscribe, getSnapshot, () => 'standard' as const)
  return { preference, setPreference: setLayoutPreference }
}
